<!-- CustomCheckbox.vue -->
<template>
  <div class="checkbox">
    <input
      :id="'checkbox-' + id"
      class="checkbox__input"
      type="checkbox"
      v-model="checked"
      :disabled="disabled"
    />
    <label :for="'checkbox-' + id" v-if="label" class="checkbox__label">{{
      label
    }}</label>
  </div>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid';
import { computed, toRefs } from 'vue';

const id = uuid().toString();

interface Props {
  modelValue: boolean;
  disabled?: boolean;
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  disabled: false
});

const { disabled, label } = toRefs(props);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const checked = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});
</script>

<style scoped lang="scss">
.checkbox {
  display: inline-flex;
  align-items: center;
  gap: $space-2;

  &__label {
    color: $color-text-darker;
    font-size: $link-s-font-size;
    line-height: $link-s-line-height;
    cursor: pointer;
  }

  &__input {
    appearance: none;
    width: $space-4;
    height: $space-4;
    border: 1px solid $color-neutral-200;
    border-radius: $radius-2;
    position: relative;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: $color-primary-500;
    }

    &:checked {
      border-color: $color-primary-500;
      background-color: $color-primary-500;

      &::after {
        content: '';
        position: absolute;
        left: calc($space-4 * 0.28);
        top: calc($space-4 * 0.065);
        width: calc($space-4 * 0.2);
        height: calc($space-4 * 0.5);
        border: solid $color-text-darker;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:disabled {
      border-color: $color-neutral-200;
      background-color: $color-neutral-50;
      cursor: not-allowed;

      &::after {
        border-color: $color-text-lighter;
      }
    }
  }
}

.checkbox__input:disabled + .checkbox__label {
  color: $color-text-lighter;
}
</style>
